import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Building2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal bloqueante para usuarios "comercio" sin datos completos.
 * Pide: CUIT, Condición IVA, Razón Social, Dirección (calle+número), Ciudad, Provincia.
 * Opcional: Entre calles, Link Google Maps.
 */
export default function BusinessProfileGate() {
  const auth = useAuth() || {};
  const user = auth.user;
  const refreshUser = auth.refreshUser;
  const [completed, setCompleted] = useState(false); // Optimistic: cerrar inmediatamente al guardar OK
  const [form, setForm] = useState({
    cuit: '', condicionIva: 'Responsable Inscripto', businessName: '',
    phone: '',
    address: '', addressNumber: '', city: '', province: 'Córdoba',
    betweenStreets: '', googleMapsLink: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [cities, setCities] = useState([]);

  const customerType = user?.customer_type || user?.customerType;

  // Determinar qué falta: si comercio y le falta algún campo crítico.
  // EXCEPCIÓN: sellers (role='seller') NO se les pide completar perfil B2B
  // porque ellos eligen el cliente con quien operan desde su panel.
  const missing = (() => {
    try {
      if (!user || customerType !== 'comercio') return [];
      if (user.role === 'seller' || user.role === 'admin') return [];
      const m = [];
      if (!user.cuit) m.push('cuit');
      if (!user.condicionIva) m.push('condicionIva');
      if (!user.businessName) m.push('businessName');
      if (!user.phone) m.push('phone');
      if (!user.address) m.push('address');
      if (!user.city) m.push('city');
      if (!user.province) m.push('province');
      return m;
    } catch (e) {
      return [];
    }
  })();
  const needsBusiness = missing.length > 0;

  useEffect(() => {
    if (user) {
      setForm(prev => ({
        ...prev,
        cuit: user.cuit || '',
        condicionIva: user.condicionIva && user.condicionIva !== 'Consumidor final' ? user.condicionIva : 'Responsable Inscripto',
        businessName: user.businessName || user.name || '',
        phone: user.phone || '',
        address: user.address || '',
        addressNumber: user.addressNumber || '',
        city: user.city || '',
        province: user.province || 'Córdoba',
        betweenStreets: user.betweenStreets || '',
        googleMapsLink: user.googleMapsLink || '',
      }));
    }
  }, [user]);

  useEffect(() => {
    if (needsBusiness) {
      axios.get(`${API}/delivery-cities`).then(r => setCities((r.data.cities || []).map(c => c.toString()))).catch(() => {});
    }
  }, [needsBusiness]);

  if (completed || !needsBusiness) return null;

  const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async () => {
    setError('');
    const cuitClean = (form.cuit || '').replace(/\D/g, '');
    if (cuitClean.length !== 11) { setError('CUIT inválido. 11 dígitos.'); return; }
    if (!form.businessName.trim()) { setError('Razón social requerida.'); return; }
    const phoneClean = (form.phone || '').replace(/\D/g, '');
    if (phoneClean.length < 8) { setError('WhatsApp inválido. Mínimo 8 dígitos.'); return; }
    if (!form.address.trim() || !form.addressNumber.trim()) { setError('Calle y número requeridos.'); return; }
    if (!form.city.trim()) { setError('Ciudad requerida.'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/user/profile`, {
        customerType: 'comercio',
        cuit: cuitClean,
        condicionIva: form.condicionIva,
        businessName: form.businessName.trim(),
        phone: phoneClean,
        address: form.address.trim(),
        addressNumber: form.addressNumber.trim(),
        city: form.city.trim(),
        province: form.province,
        betweenStreets: form.betweenStreets,
        googleMapsLink: form.googleMapsLink,
      }, { withCredentials: true });
      // Optimista: cerrar el modal inmediatamente
      setCompleted(true);
      if (typeof refreshUser === 'function') {
        // Pequeño delay para que el backend persista, luego refresh
        setTimeout(() => { refreshUser().catch(() => {}); }, 250);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="business-gate">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl my-8">
        <div className="flex items-center gap-2 mb-1">
          <Building2 size={22} className="text-emerald-600" />
          <h2 className="text-xl font-black text-zinc-900">Completá los datos de tu comercio</h2>
        </div>
        <p className="text-sm text-zinc-600 mb-4">
          Faltan datos para poder entregarte el pedido. Los pedimos una sola vez.
        </p>

        {error && (
          <div className="mb-3 px-3 py-2 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700 flex items-start gap-2">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" /> {error}
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Razón social / Nombre comercio *</label>
            <input value={form.businessName} onChange={(e) => upd('businessName', e.target.value)}
              placeholder="Ej: Distribuidora El Sol SRL"
              className="w-full px-3 py-2.5 border border-zinc-300 rounded-lg text-sm" data-testid="gate-business-name" />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">CUIT *</label>
              <input value={form.cuit} onChange={(e) => upd('cuit', e.target.value.replace(/\D/g, ''))}
                inputMode="numeric" maxLength={11} placeholder="20123456789"
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-lg text-sm font-mono" data-testid="gate-cuit" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Cond. IVA *</label>
              <select value={form.condicionIva} onChange={(e) => upd('condicionIva', e.target.value)}
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-lg text-sm bg-white" data-testid="gate-iva">
                <option>Responsable Inscripto</option>
                <option>Monotributo</option>
                <option>Exento</option>
                <option>No Responsable</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">📱 WhatsApp *</label>
            <input value={form.phone}
              onChange={(e) => upd('phone', e.target.value.replace(/[^\d]/g, ''))}
              inputMode="tel" placeholder="3571555555"
              className="w-full px-3 py-2.5 border border-zinc-300 rounded-lg text-sm font-mono"
              data-testid="gate-phone" />
            <p className="text-[10px] text-slate-400 mt-1">Sólo números, sin código de país ni guiones. Ej: 3571555555</p>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Calle *</label>
              <input value={form.address} onChange={(e) => upd('address', e.target.value)}
                placeholder="Av. San Martín"
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-lg text-sm" data-testid="gate-address" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">N° *</label>
              <input value={form.addressNumber} onChange={(e) => upd('addressNumber', e.target.value)}
                placeholder="123"
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-lg text-sm" data-testid="gate-address-number" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Ciudad *</label>
              {cities.length > 0 ? (
                <select value={form.city} onChange={(e) => upd('city', e.target.value)}
                  className="w-full px-3 py-2.5 border border-zinc-300 rounded-lg text-sm bg-white" data-testid="gate-city">
                  <option value="">— Elegí —</option>
                  {cities.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              ) : (
                <input value={form.city} onChange={(e) => upd('city', e.target.value)}
                  className="w-full px-3 py-2.5 border border-zinc-300 rounded-lg text-sm" data-testid="gate-city" />
              )}
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Provincia *</label>
              <select value={form.province} onChange={(e) => upd('province', e.target.value)}
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-lg text-sm bg-white" data-testid="gate-province">
                {['Córdoba','Buenos Aires','CABA','Catamarca','Chaco','Chubut','Corrientes','Entre Ríos','Formosa','Jujuy','La Pampa','La Rioja','Mendoza','Misiones','Neuquén','Río Negro','Salta','San Juan','San Luis','Santa Cruz','Santa Fe','Santiago del Estero','Tierra del Fuego','Tucumán'].map(p =>
                  <option key={p} value={p}>{p}</option>
                )}
              </select>
            </div>
          </div>

          <details>
            <summary className="cursor-pointer text-xs text-slate-500 font-medium hover:text-slate-700">📍 Ubicación complicada? (opcional)</summary>
            <div className="mt-2 space-y-2 pl-2 border-l-2 border-slate-100">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">Entre calles</label>
                <input value={form.betweenStreets} onChange={(e) => upd('betweenStreets', e.target.value)}
                  placeholder="Ej: entre Mitre y Belgrano"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm" data-testid="gate-between" />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">Link Google Maps</label>
                <input value={form.googleMapsLink} onChange={(e) => upd('googleMapsLink', e.target.value)}
                  placeholder="https://maps.app.goo.gl/..."
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm font-mono" data-testid="gate-maps" />
              </div>
            </div>
          </details>
        </div>

        <button onClick={submit} disabled={saving}
          className="w-full mt-4 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-lg font-bold"
          data-testid="gate-submit">
          {saving ? 'Guardando...' : 'Guardar y continuar'}
        </button>
      </div>
    </div>
  );
}
